import React, { useState } from 'react';
import { Calculator, DollarSign, Layers, ChevronDown, ChevronUp } from 'lucide-react';

const TIPOS = [
  { id: 'LEGAL', label: 'Legal', icon: Calculator, color: 'blue' },
  { id: 'FIJA',  label: 'Fija',  icon: DollarSign, color: 'violet' },
  { id: 'MIXTA', label: 'Mixta', icon: Layers,     color: 'teal' },
];

const COLOR = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700',   icon: 'text-blue-600'   },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700', icon: 'text-violet-600' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-700',   badge: 'bg-teal-100 text-teal-700',   icon: 'text-teal-600'   },
};

function Formula({ label, formula, result, note }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
      <div className="bg-gray-900 rounded-lg px-4 py-2.5 font-mono text-xs text-green-400 leading-relaxed">
        {formula}
      </div>
      {result && (
        <p className="text-xs text-gray-500 pl-1">→ {result}</p>
      )}
      {note && (
        <p className="text-xs text-amber-600 pl-1 italic">{note}</p>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{title}</p>
      {children}
    </div>
  );
}

function TipoCard({ tipo, activeId, onToggle }) {
  const c     = COLOR[tipo.color];
  const Icon  = tipo.icon;
  const open  = activeId === tipo.id;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${c.border}`}>
      <button
        onClick={() => onToggle(tipo.id)}
        className={`w-full flex items-center justify-between px-4 py-3 ${open ? c.bg : 'bg-white hover:bg-gray-50'} transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-lg ${open ? 'bg-white bg-opacity-70' : 'bg-gray-100'}`}>
            <Icon size={15} className={open ? c.icon : 'text-gray-500'} />
          </div>
          <div className="text-left">
            <p className={`text-sm font-bold ${open ? c.text : 'text-gray-800'}`}>
              Bonificación {tipo.label}
            </p>
            <p className="text-xs text-gray-400">
              {tipo.id === 'LEGAL' && 'Art. 223 CT-RD · 10% utilidad neta'}
              {tipo.id === 'FIJA'  && 'Monto global, por salario o por empleado'}
              {tipo.id === 'MIXTA' && 'Legal o distribución según fondo disponible'}
            </p>
          </div>
        </div>
        {open ? <ChevronUp size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />}
      </button>

      {open && (
        <div className={`px-4 pb-4 pt-3 ${c.bg} border-t ${c.border} space-y-5`}>

          {/* ── LEGAL ── */}
          {tipo.id === 'LEGAL' && (
            <>
              <Section title="1 · Salario base">
                <Formula
                  label="Salario anual promedio"
                  formula={"salario = SUM(salario_mensual_jan .. salario_mensual_dec) / 12"}
                  result="Siempre dividido entre 12, aunque el empleado haya trabajado fracción del año"
                  note="Para nóminas quincenales se suman las dos quincenas del mes antes de dividir"
                />
              </Section>

              <Section title="2 · Tope legal (Art. 223 CT-RD)">
                <Formula
                  label="Días según antigüedad al 31-dic del año fiscal"
                  formula={"tope_dias = años_servicio >= 3 ? 60 : 45"}
                />
                <Formula
                  label="Tope individual"
                  formula={"tope_monto = salario / 23.83 × tope_dias"}
                  result="Salario diario × días correspondientes (23.83 = días hábiles del mes)"
                />
              </Section>

              <Section title="3 · Monto a distribuir">
                <Formula
                  label="Fondo total disponible"
                  formula={"monto_distribuir = utilidad_neta × (porcentaje_utilidad / 100)"}
                  result="Por ley el mínimo es 10% de la utilidad neta anual"
                />
              </Section>

              <Section title="4 · Regla de pago (mutuamente excluyente)">
                <Formula
                  label="Condición"
                  formula={"si  monto_distribuir >= SUM(tope_monto de todos los empleados)\n    → cada empleado cobra su tope_monto\nsi  monto_distribuir <  SUM(tope_monto)\n    → cada empleado cobra proporción por peso"}
                />
                <Formula
                  label="Caso distribución proporcional"
                  formula={"peso_emp    = salario_emp\nmonto_emp   = monto_distribuir × (peso_emp / SUM(peso))"}
                  note="El peso es el salario promedio de cada empleado"
                />
              </Section>

              <Section title="5 · Descuento INFOTEP">
                <Formula
                  label="Retención al empleado"
                  formula={"infotep = monto_final × tasa_infotep\nneto    = monto_final × (1 - tasa_infotep)"}
                  result="Tasa configurable en Configuración de Nómina → Parámetros (por defecto 0.5%)"
                />
              </Section>
            </>
          )}

          {/* ── FIJA ── */}
          {tipo.id === 'FIJA' && (
            <>
              <Section title="Salario base (igual que Legal)">
                <Formula
                  label="Salario anual promedio"
                  formula={"salario = SUM(salario_mensual_jan .. salario_mensual_dec) / 12"}
                />
              </Section>

              <Section title="Métodos de cálculo disponibles">
                <Formula
                  label="A · Por antigüedad (45/60 días)"
                  formula={"monto = salario / 23.83 × tope_dias"}
                  result="Mismo cálculo que el tope legal; cada empleado recibe exactamente ese monto"
                />
                <Formula
                  label="B · Monto global · Igualitario"
                  formula={"monto_emp = monto_global / total_empleados_activos"}
                  result="El fondo se divide en partes iguales sin importar salario ni antigüedad"
                />
                <Formula
                  label="C · Monto global · Proporcional al salario"
                  formula={"peso_emp  = salario_emp\nmonto_emp = monto_global × (peso_emp / SUM(peso))"}
                />
                <Formula
                  label="D · Monto global · Por desempeño"
                  formula={"monto_emp = monto_global × (puntaje_emp / SUM(puntajes))"}
                  note="Los puntajes (0–100) se ingresan manualmente por empleado y se recalculan con el botón 'Recalcular Puntajes'"
                />
                <Formula
                  label="E · N × Salario mensual"
                  formula={"monto_emp = salario_emp × cantidad_salarios"}
                  result="Ej: cantidad_salarios = 1.5 → paga mes y medio a cada empleado"
                />
                <Formula
                  label="F · Monto fijo por empleado"
                  formula={"monto_emp = monto_por_empleado  (igual para todos)"}
                />
              </Section>

              <Section title="Descuento INFOTEP">
                <Formula
                  label="Retención"
                  formula={"infotep = monto_final × tasa_infotep\nneto    = monto_final × (1 - tasa_infotep)"}
                />
              </Section>
            </>
          )}

          {/* ── MIXTA ── */}
          {tipo.id === 'MIXTA' && (
            <>
              <Section title="1 · Salario y tope (igual que Legal)">
                <Formula
                  label="Salario"
                  formula={"salario = SUM(salario_mensual_jan .. salario_mensual_dec) / 12"}
                />
                <Formula
                  label="Tope individual"
                  formula={"tope_monto = salario / 23.83 × (años >= 3 ? 60 : 45)"}
                />
              </Section>

              <Section title="2 · Monto a distribuir">
                <Formula
                  label="Fondo legal"
                  formula={"monto_distribuir = utilidad_neta × (porcentaje_utilidad / 100)"}
                />
              </Section>

              <Section title="3 · Regla de pago (mutuamente excluyente)">
                <Formula
                  label="El fondo cubre todos los topes"
                  formula={"si  monto_distribuir >= SUM(tope_monto)\n    → cada empleado cobra su tope_monto  [Bonif. Legal]"}
                  result="El fondo es suficiente para pagar el máximo legal a todos"
                />
                <Formula
                  label="El fondo NO cubre todos los topes"
                  formula={"si  monto_distribuir <  SUM(tope_monto)\n    → monto_emp = monto_distribuir × (salario_emp / SUM(salarios))  [Distribución]"}
                  result="Se reparte lo disponible proporcionalmente; nadie supera su tope"
                  note="Nunca se pagan ambas columnas simultáneamente — es una o la otra"
                />
              </Section>

              <Section title="4 · Descuento INFOTEP">
                <Formula
                  label="Retención"
                  formula={"infotep = monto_final × tasa_infotep\nneto    = monto_final × (1 - tasa_infotep)"}
                />
              </Section>
            </>
          )}

        </div>
      )}
    </div>
  );
}

export default function FormulasTab({ bonificacion }) {
  const [activeId, setActiveId] = useState(bonificacion?.tipo || 'LEGAL');

  const toggle = (id) => setActiveId(prev => prev === id ? null : id);

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <Calculator size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-gray-800">Referencia de Fórmulas</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Normativa aplicada: Código de Trabajo RD · Art. 223 (bonificación) · Art. 219 (regalía pascual).
            Haz clic en un tipo para expandir sus fórmulas.
          </p>
        </div>
      </div>

      {/* Cards por tipo */}
      <div className="space-y-2">
        {TIPOS.map(t => (
          <TipoCard key={t.id} tipo={t} activeId={activeId} onToggle={toggle} />
        ))}
      </div>

      {/* Glosario */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Glosario de variables</p>
        </div>
        <div className="divide-y divide-gray-100">
          {[
            { var: 'salario',           def: 'SUM(salarios mensuales del año) / 12' },
            { var: 'tope_dias',         def: '45 días si antigüedad < 3 años · 60 días si antigüedad ≥ 3 años' },
            { var: 'tope_monto',        def: 'salario / 23.83 × tope_dias' },
            { var: 'monto_distribuir',  def: 'utilidad_neta × porcentaje_utilidad / 100' },
            { var: 'peso_emp',          def: 'salario mensual promedio del empleado' },
            { var: 'monto_final',       def: 'monto_ajustado si fue editado manualmente, de lo contrario monto_total' },
            { var: 'tasa_infotep',      def: 'Configurable en Configuración de Nómina → Parámetros (por defecto 0.5%)' },
            { var: '23.83',             def: 'Días hábiles promedio por mes (días laborables / 12)' },
          ].map(row => (
            <div key={row.var} className="flex items-start gap-4 px-4 py-2.5">
              <code className="text-xs font-mono text-violet-700 bg-violet-50 px-2 py-0.5 rounded flex-shrink-0 mt-0.5">
                {row.var}
              </code>
              <p className="text-xs text-gray-600">{row.def}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
